import type { User } from '@/store/user.ts'
import { activeMembership } from '@/routes/start/lib/activeMembership.ts'
import {
  NO_STAGE_PATH,
  ORG_PROJECTS_PATH,
  START_PATH,
  stageDashboardPath,
} from '@/routes/start/lib/startPaths.ts'

/**
 * Where a user with no usable project stage lands (docs/plans/onboarding-administratora.md,
 * "Wejście — tylko naprawdę pusta organizacja").
 *
 * - ADMIN/OWNER of an active organization with no project → the `/start` wizard;
 * - ADMIN/OWNER whose every project is archived, with a live demo → the demo
 *   (docs/plans/szablony-i-demo.md, D7, "Lądowanie po archiwizacji"): the project list
 *   hides the archive, so it would show nothing to work in;
 * - ADMIN/OWNER whose organization has projects they are not on → the company project list,
 *   which also holds the "Zarchiwizowane" section;
 * - anyone else → the "no construction site yet" screen.
 *
 * `projectsCount` comes from a normalizer, so a user cached by a build that predates it
 * lacks the field. An admin with an unknown count goes to the project list, which is
 * correct for both cases (it offers "Nowy projekt" when it is empty), rather than into a
 * wizard that would create a second project for an organization that already has one.
 */
export function resolveNoStageLanding(user: User) {
  const roleCode = activeMembership(user)?.role?.code
  const isAdmin = roleCode === 'ROLE_ORGANIZATION_ADMIN' || roleCode === 'ROLE_ORGANIZATION_OWNER'
  if (!isAdmin) return NO_STAGE_PATH

  if (user.projectsCount === 0) return START_PATH
  // Both fields come with D7; a user cached before it has neither and keeps the list.
  if (user.activeProjectsCount === 0 && user.demoStageId != null) {
    return stageDashboardPath(user.demoStageId)
  }
  return ORG_PROJECTS_PATH
}
